import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import CheckIcon from '@mui/icons-material/Check';
import BusinessIcon from '@mui/icons-material/Business';
import AddIcon from '@mui/icons-material/Add';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  getMyMemberships,
  switchActiveOrg,
  getActiveOrgId,
} from '../../api/organizations';
import { useAuthStore } from '../../stores/authStore';

/** Top-bar dropdown for switching between organizations. Hidden when
 * the user has access to only one org — no UI noise in single-tenant
 * deployments. */
export default function OrgSelector() {
  const navigate = useNavigate();
  const isAdmin = useAuthStore((s) => s.user?.is_admin ?? false);
  const { data: memberships } = useQuery({
    queryKey: ['organizations', 'me', 'memberships'],
    queryFn: getMyMemberships,
    staleTime: 60_000,
  });

  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  if (!memberships || memberships.length === 0) return null;
  if (memberships.length === 1) {
    // Single-org user — show the name as a label but no switcher.
    const only = memberships[0];
    return (
      <Tooltip title={`${only.organization_name}${only.role === 'org_admin' ? ' · אדמין' : ''}`} arrow>
        <Chip
          size="small"
          icon={<BusinessIcon sx={{ fontSize: 16 }} />}
          label={only.organization_title || only.organization_name}
          variant="outlined"
          sx={{ fontWeight: 600, maxWidth: 240 }}
        />
      </Tooltip>
    );
  }

  // Multi-org user — the dropdown highlights the active row.
  const storedActiveId = getActiveOrgId();
  const active =
    memberships.find((m) => m.organization_id === storedActiveId) ??
    memberships.find((m) => m.is_primary) ??
    memberships[0];

  return (
    <Box>
      <Button
        size="small"
        onClick={(e) => setAnchor(e.currentTarget)}
        startIcon={<BusinessIcon />}
        variant="outlined"
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          maxWidth: 260,
          color: 'text.primary',
          borderColor: 'divider',
          '&:hover': { borderColor: 'primary.main' },
        }}
      >
        {active.organization_title || active.organization_name}
      </Button>
      <Menu
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        PaperProps={{ sx: { minWidth: 280, maxWidth: 360 } }}
      >
        {memberships.map((m) => {
          const isActive = m.organization_id === active.organization_id;
          return (
            <MenuItem
              key={m.organization_id}
              selected={isActive}
              onClick={() => {
                if (!isActive) switchActiveOrg(m.organization_id);
                else setAnchor(null);
              }}
            >
              <ListItemIcon>
                {isActive && <CheckIcon fontSize="small" />}
              </ListItemIcon>
              <ListItemText
                primary={m.organization_title || m.organization_name}
                secondary={
                  m.is_primary
                    ? (m.role === 'org_admin' ? 'ארגון ראשי · אדמין' : 'ארגון ראשי')
                    : (m.role === 'org_admin' ? 'אדמין' : 'חבר')
                }
              />
            </MenuItem>
          );
        })}
        {isAdmin && (
          <>
            <Divider />
            <MenuItem
              onClick={() => {
                setAnchor(null);
                navigate('/setup-org');
              }}
            >
              <ListItemIcon><AddIcon fontSize="small" /></ListItemIcon>
              <ListItemText
                primary="הוסף ארגון חדש"
                secondary="פתח אשף הקמה"
              />
            </MenuItem>
          </>
        )}
      </Menu>
    </Box>
  );
}
